import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthTokenService } from '../services/auth-token.service';
import { AuthRepositoryPort } from '../../domain/repositories/auth.repository.port';
import { AuthSession } from '../../domain/entities/auth-session.entity';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly tokenService = inject(AuthTokenService);
  private readonly authRepository = inject(AuthRepositoryPort);
  private readonly router = inject(Router);

  private isRefreshing = false;
  private readonly refreshSubject = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isAuthUrl(req.url)) {
      return next.handle(req);
    }

    const token = this.tokenService.accessToken();
    const authReq = token ? this.addToken(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401(req, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(req, token!))),
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    const session = this.tokenService.session();
    if (!session?.refreshToken) {
      return this.redirectToLogin();
    }

    return this.authRepository.refreshToken(session.accessToken.token, session.refreshToken).pipe(
      switchMap((newSession: AuthSession) => {
        this.isRefreshing = false;
        this.tokenService.setSession(newSession);
        this.refreshSubject.next(newSession.accessToken.token);
        return next.handle(this.addToken(req, newSession.accessToken.token));
      }),
      catchError(() => {
        this.isRefreshing = false;
        return this.redirectToLogin();
      }),
    );
  }

  private addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
  }

  private isAuthUrl(url: string): boolean {
    return url.includes('/auth/');
  }

  private redirectToLogin(): Observable<never> {
    this.tokenService.clearSession();
    this.router.navigate(['/login']);
    return EMPTY;
  }
}
